import { Navigate, useNavigate, useParams } from 'react-router-dom'
import { useEffect } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '../contexts/ToastContext'
import { getInvitationPreview, acceptInvitation } from '../lib/api/circles'
import { CIRCLE_TYPES } from '../lib/circleCapabilities'
import { Logo } from '../components/shell/Logo'
import { CotApiError } from '../lib/api/client'
import { PersonProvider, usePerson } from '../contexts/PersonContext'
import { canEnterCommunity } from '../lib/verificationStatus'
import { consumeReturnPath, rememberReturnPath, withReturnPath } from '../lib/returnPath'

/**
 * Warm-paper invite acceptance page (COTC_Circles R3, DESIGN). Standalone (no shell).
 * Unauthenticated visitors are bounced to /login. Onboarded families see a circle preview and
 * accept, joining the circle with a vouch from the inviter.
 */
export function InvitationAcceptPage() {
  const { token } = useParams<{ token: string }>()
  const { isAuthenticated, isLoading: authLoading } = useAuth()
  const destination = `/invitations/${token ?? ''}`
  useEffect(() => { rememberReturnPath(destination) }, [destination])
  if (authLoading) return <Centered>Loading…</Centered>
  if (!isAuthenticated) return <Navigate to={withReturnPath('/login', destination)} replace state={{ from: destination }} />
  return <PersonProvider><InvitationPreview /></PersonProvider>
}

function InvitationPreview() {
  const { token } = useParams<{ token: string }>()
  const { loading, error, refresh, needsOnboarding, verification, role, setActivePerson, accountHolderId } = usePerson()
  const navigate = useNavigate()
  const { flash } = useToast()
  const destination = `/invitations/${token ?? ''}`
  const ready = !loading && !error && !needsOnboarding && canEnterCommunity(verification) && role === 'parent'

  const preview = useQuery({
    queryKey: ['community', 'invitation', token],
    queryFn: () => getInvitationPreview(token as string),
    enabled: ready && !!token,
    retry: false,
  })

  const accept = useMutation({
    mutationFn: () => acceptInvitation(token as string),
    onSuccess: () => {
      flash('You’re in — welcome to the circle.')
      consumeReturnPath()
      navigate('/')
    },
    onError: (err) => {
      const status = err instanceof CotApiError ? err.status : undefined
      flash(status === 409 ? 'Your family is already in this circle.' : 'That invite could not be accepted.')
      if (status === 409) { consumeReturnPath(); navigate('/') }
    },
  })

  if (loading) {
    return <Centered>Loading…</Centered>
  }
  if (needsOnboarding) return <Navigate to={withReturnPath('/signup', destination)} replace />
  if (error) return <Centered><span role="alert">Your account could not be loaded.</span><button className="mt-3 block underline" onClick={() => void refresh()}>Retry account</button></Centered>
  if (!canEnterCommunity(verification)) return <Navigate to={withReturnPath('/verification', destination)} replace />
  if (role !== 'parent') return <Centered><span>A parent or guardian must accept a family invitation. Switch using the guardian PIN.</span><button className="mt-3 block underline" onClick={() => setActivePerson(accountHolderId)}>Switch to guardian profile</button></Centered>

  const meta = preview.data ? CIRCLE_TYPES.find((t) => t.typeName === preview.data.type) : undefined

  return (
    <div className="flex min-h-screen items-center justify-center bg-paper-page px-4 py-10">
      <div className="w-full max-w-[460px] rounded-[14px] border border-line-soft bg-paper-surface px-8 py-9 shadow-modal animate-rise">
        <div className="mb-6 flex justify-center">
          <Logo />
        </div>

        {preview.isLoading && <p className="text-center text-[14px] text-ink-muted">Opening the invitation…</p>}

        {preview.isError && (
          <div className="text-center">
            <h1 className="mb-2 font-serif text-[24px] font-medium text-ink">{preview.error instanceof CotApiError && (preview.error.status === 404 || preview.error.status === 410) ? 'This invite isn’t valid' : 'The invitation could not be loaded'}</h1>
            <p className="mb-6 text-[13.5px] leading-relaxed text-ink-muted">
              {preview.error instanceof CotApiError && (preview.error.status === 404 || preview.error.status === 410) ? 'It may have expired, been used, or been withdrawn. Ask the family who sent it for a fresh link.' : 'Check your connection and try again. Your invitation has been kept.'}
            </p>
            <button
              type="button"
              onClick={() => void preview.refetch()}
              className="rounded-btn bg-ink px-[18px] py-3 text-[13.5px] font-medium text-paper-panel"
            >
              Retry invitation
            </button>
          </div>
        )}

        {preview.data && (
          <div className="text-center">
            <p className="mb-2 font-mono text-[11px] uppercase tracking-label text-ink-faint">A circle invitation</p>
            <h1 className="mb-2 font-serif text-[26px] font-medium text-ink">{preview.data.circleName}</h1>
            <div className="mb-5 flex items-center justify-center gap-2">
              <span
                className="h-[9px] w-[9px] rounded-full border-2"
                style={{ borderColor: meta?.dot ?? '#b9704f' }}
              />
              <span className="font-mono text-[11.5px] uppercase tracking-label text-ink-faint">
                {meta?.label ?? preview.data.type} · {preview.data.memberFamilyCount}{' '}
                {preview.data.memberFamilyCount === 1 ? 'family' : 'families'}
              </span>
            </div>
            <p className="mb-6 text-[13.5px] leading-relaxed text-ink-muted">
              {preview.data.inviterFamilyName} invited your family to this circle. Joining vouches for
              you both — and lets your kids see each other.
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                type="button"
                disabled={accept.isPending}
                onClick={() => accept.mutate()}
                className="rounded-btn bg-terra px-[18px] py-3 text-[13.5px] font-medium text-paper-surface hover:bg-terra-deep"
              >
                {accept.isPending ? 'Joining…' : 'Accept invitation'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/')}
                className="rounded-btn border border-line px-[18px] py-3 text-[13.5px] font-medium text-ink-muted hover:border-terra hover:text-terra-deep"
              >
                Not now
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-paper-page">
      <p className="font-mono text-[11px] uppercase tracking-label text-ink-faint">{children}</p>
    </div>
  )
}
